import React, { ReactNode } from 'react';
import DashboardCard from '@/components/ui/DashboardCard';

interface EmptyStateLayoutProps {
  icon: ReactNode;
  title: string;
  description?: string; // Optional description text
  children: ReactNode; // The form or content specific to each generator
  variant?: 'purple' | 'blue' | 'green' | 'default' | 'amber' | 'indigo';
  fullHeight?: boolean; // Whether to use viewport-based height (default: true for backward compatibility)
}

/**
 * Consistent centered empty state layout used across all generators
 * Provides the card structure, icon, title, and form container
 */
export const EmptyStateLayout: React.FC<EmptyStateLayoutProps> = ({
  icon,
  title,
  description,
  children,
  variant = 'purple',
  fullHeight = true
}) => {
  // Dynamic theming based on variant
  const getIconBackground = (variant: string) => {
    switch (variant) {
      case 'amber':
        return 'bg-gradient-to-br from-amber-500/20 to-yellow-500/20';
      case 'blue':
        return 'bg-gradient-to-br from-blue-500/20 to-cyan-500/20';
      case 'green':
        return 'bg-gradient-to-br from-green-500/20 to-emerald-500/20';
      case 'indigo':
        return 'bg-gradient-to-br from-indigo-500/20 to-purple-500/20';
      case 'purple':
      default:
        return 'bg-gradient-to-br from-purple-500/20 to-blue-500/20';
    }
  };

  return (
    <div className={`flex items-center justify-center ${fullHeight ? 'min-h-[calc(100vh-200px)]' : ''}`}>
      <div className="w-full max-w-2xl mx-auto px-4">
        <DashboardCard variant={variant} className="p-8 md:p-12">
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <div className={`p-4 ${getIconBackground(variant)} rounded-2xl`}>
                {icon}
              </div>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {title}
            </h2>
            {description && (
              <p className="text-center text-gray-600 dark:text-gray-400 text-sm mb-6">
                {description}
              </p>
            )}
          </div>
          
          {/* App-specific content (forms, buttons, etc.) */}
          {children}
        </DashboardCard>
      </div>
    </div>
  );
}; 